import { newFreeDrawElement } from "@excalidraw/element/newElement";
import { pointFrom } from "@excalidraw/math";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { LocalPoint } from "@excalidraw/math";

import { LINE_EPSILON, polygon, safeLine } from "./helpers";
import { FREEDRAW_STROKE_WIDTH, SHAPE_STROKE_WIDTH } from "./strokeConstants";

import type { ShapeDefinition } from "./types";

// ---------- Cylinder (Database) ----------
const CYL_W = 80;
const CYL_H = 100;
const CYL_CAP = 20;
const CYL_BODY = CYL_H - CYL_CAP;

const CylinderThumbnail = () => (
  <svg
    viewBox="0 0 80 100"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
  >
    <ellipse cx="40" cy="10" rx="38" ry="8" />
    <line x1="2" y1="10" x2="2" y2="90" />
    <line x1="78" y1="10" x2="78" y2="90" />
    <path d="M 2 90 A 38 8 0 0 0 78 90" />
  </svg>
);

const cylinder: ShapeDefinition = {
  id: "cylinder",
  category: "flowchart",
  name: "Cylinder",
  width: CYL_W,
  height: CYL_H,
  thumbnail: <CylinderThumbnail />,
  build: (groupId, ox, oy) => [
    {
      type: "ellipse",
      x: ox,
      y: oy,
      width: CYL_W,
      height: CYL_CAP,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
    {
      type: "line",
      x: ox,
      y: oy + CYL_CAP / 2,
      width: LINE_EPSILON,
      height: CYL_BODY,
      points: [
        pointFrom<LocalPoint>(0, 0),
        pointFrom<LocalPoint>(LINE_EPSILON, CYL_BODY),
      ],
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
    {
      type: "line",
      x: ox + CYL_W,
      y: oy + CYL_CAP / 2,
      width: LINE_EPSILON,
      height: CYL_BODY,
      points: [
        pointFrom<LocalPoint>(0, 0),
        pointFrom<LocalPoint>(LINE_EPSILON, CYL_BODY),
      ],
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
    {
      type: "ellipse",
      x: ox,
      y: oy + CYL_H - CYL_CAP,
      width: CYL_W,
      height: CYL_CAP,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
  ],
};

// ---------- Process (rectangle) ----------
const processShape: ShapeDefinition = {
  id: "process",
  category: "flowchart",
  name: "Process",
  width: 120,
  height: 80,
  thumbnail: (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="114" height="74" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    {
      type: "rectangle",
      x: ox,
      y: oy,
      width: 120,
      height: 80,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
  ],
};

// ---------- Decision (diamond) ----------
const decision: ShapeDefinition = {
  id: "decision",
  category: "flowchart",
  name: "Decision",
  width: 120,
  height: 100,
  thumbnail: (
    <svg
      viewBox="0 0 120 100"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <polygon points="60,3 117,50 60,97 3,50" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    {
      type: "diamond",
      x: ox,
      y: oy,
      width: 120,
      height: 100,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
  ],
};

// ---------- Terminal (pill / stadium) ----------
// Built as a freedraw element so it has only bounding-box selection
// handles (8) instead of one handle per vertex (which line elements
// always render via LinearElementEditor, see
// packages/excalidraw/renderer/interactiveScene.ts:1777-1788).
// Excalidraw caps rectangle corner radius at 25% of the shorter side
// (packages/element/src/utils.ts:485), so a single rounded rectangle
// can't reach pill proportions; freedraw's perfect-freehand smoothing
// gives a clean uniform-width outline through dense input points.
const TERM_W = 120;
const TERM_H = 60;
const TERM_R = TERM_H / 2;
const TERM_CX_LEFT = TERM_R;
const TERM_CX_RIGHT = TERM_W - TERM_R;
const TERM_BODY_SAMPLE_COUNT = 6;
const TERM_SEMICIRCLE_STEP_DEG = 7.5;

const buildPillStrokePoints = (): LocalPoint[] => {
  const points: LocalPoint[] = [];
  // Top body, left → right
  for (let i = 0; i < TERM_BODY_SAMPLE_COUNT; i++) {
    const t = i / (TERM_BODY_SAMPLE_COUNT - 1);
    points.push(
      pointFrom<LocalPoint>(
        TERM_CX_LEFT + (TERM_CX_RIGHT - TERM_CX_LEFT) * t,
        0,
      ),
    );
  }
  // Right semicircle interior, top → bottom
  for (
    let a = TERM_SEMICIRCLE_STEP_DEG;
    a < 180;
    a += TERM_SEMICIRCLE_STEP_DEG
  ) {
    const rad = (a * Math.PI) / 180;
    points.push(
      pointFrom<LocalPoint>(
        TERM_CX_RIGHT + TERM_R * Math.sin(rad),
        TERM_R - TERM_R * Math.cos(rad),
      ),
    );
  }
  // Bottom body, right → left (skip first to avoid duplicating the semicircle endpoint)
  for (let i = 0; i < TERM_BODY_SAMPLE_COUNT; i++) {
    const t = i / (TERM_BODY_SAMPLE_COUNT - 1);
    points.push(
      pointFrom<LocalPoint>(
        TERM_CX_RIGHT + (TERM_CX_LEFT - TERM_CX_RIGHT) * t,
        TERM_H,
      ),
    );
  }
  // Left semicircle interior, bottom → top
  for (
    let a = 180 + TERM_SEMICIRCLE_STEP_DEG;
    a < 360;
    a += TERM_SEMICIRCLE_STEP_DEG
  ) {
    const rad = (a * Math.PI) / 180;
    points.push(
      pointFrom<LocalPoint>(
        TERM_CX_LEFT + TERM_R * Math.sin(rad),
        TERM_R - TERM_R * Math.cos(rad),
      ),
    );
  }
  // Close the loop back to the start
  points.push(pointFrom<LocalPoint>(TERM_CX_LEFT, 0));
  return points;
};

const TERM_PILL_POINTS: readonly LocalPoint[] = buildPillStrokePoints();
// Uniform pressure → uniform stroke width (perfect-freehand wouldn't
// vary thickness without simulatePressure anyway, but be explicit).
const TERM_PRESSURES: readonly number[] = TERM_PILL_POINTS.map(() => 0.5);

const terminal: ShapeDefinition = {
  id: "terminal",
  category: "flowchart",
  name: "Terminal",
  width: TERM_W,
  height: TERM_H,
  thumbnail: (
    <svg
      viewBox="0 0 120 60"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
    >
      <rect x="3" y="3" width="114" height="54" rx="27" ry="27" />
    </svg>
  ),
  buildElements: (groupId, ox, oy): ExcalidrawElement[] => [
    newFreeDrawElement({
      type: "freedraw",
      x: ox,
      y: oy,
      width: TERM_W,
      height: TERM_H,
      points: TERM_PILL_POINTS,
      pressures: TERM_PRESSURES,
      simulatePressure: false,
      // Compensated nominal value so the freedraw stroke renders at the
      // same visual width as polygon/rectangle/ellipse shapes; see
      // strokeConstants.ts for the formula.
      strokeWidth: FREEDRAW_STROKE_WIDTH,
      groupIds: [groupId],
    }),
  ],
};

// ---------- Parallelogram (Data) ----------
const parallelogram: ShapeDefinition = {
  id: "parallelogram",
  category: "flowchart",
  name: "Data",
  width: 120,
  height: 80,
  thumbnail: (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <polygon points="23,3 117,3 97,77 3,77" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    polygon(
      [
        [20, 0],
        [120, 0],
        [100, 80],
        [0, 80],
      ],
      groupId,
      ox,
      oy,
    ),
  ],
};

// ---------- Manual Input (slanted top) ----------
const manualInput: ShapeDefinition = {
  id: "manual-input",
  category: "flowchart",
  name: "Manual Input",
  width: 120,
  height: 80,
  thumbnail: (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <polygon points="3,23 117,3 117,77 3,77" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    polygon(
      [
        [0, 20],
        [120, 0],
        [120, 80],
        [0, 80],
      ],
      groupId,
      ox,
      oy,
    ),
  ],
};

// ---------- Predefined Process (rect with two inner verticals) ----------
const predefinedProcess: ShapeDefinition = {
  id: "predefined-process",
  category: "flowchart",
  name: "Predefined Process",
  width: 120,
  height: 80,
  thumbnail: (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="114" height="74" />
      <line x1="15" y1="3" x2="15" y2="77" />
      <line x1="105" y1="3" x2="105" y2="77" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    {
      type: "rectangle",
      x: ox,
      y: oy,
      width: 120,
      height: 80,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
    safeLine(ox + 12, oy, 0, 80, groupId),
    safeLine(ox + 108, oy, 0, 80, groupId),
  ],
};

// ---------- Connector (small circle) ----------
const connector: ShapeDefinition = {
  id: "connector",
  category: "flowchart",
  name: "Connector",
  width: 60,
  height: 60,
  thumbnail: (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
    >
      <circle cx="50" cy="50" r="46" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    {
      type: "ellipse",
      x: ox,
      y: oy,
      width: 60,
      height: 60,
      groupIds: [groupId],
      strokeWidth: SHAPE_STROKE_WIDTH,
    },
  ],
};

// ---------- Off-Page Connector (home plate / pentagon) ----------
const offPageConnector: ShapeDefinition = {
  id: "off-page-connector",
  category: "flowchart",
  name: "Off-Page Connector",
  width: 100,
  height: 100,
  thumbnail: (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <polygon points="3,3 97,3 97,67 50,97 3,67" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    polygon(
      [
        [0, 0],
        [100, 0],
        [100, 65],
        [50, 100],
        [0, 65],
      ],
      groupId,
      ox,
      oy,
    ),
  ],
};

// ---------- Manual Operation (inverted trapezoid) ----------
const manualOperation: ShapeDefinition = {
  id: "manual-operation",
  category: "flowchart",
  name: "Manual Operation",
  width: 120,
  height: 80,
  thumbnail: (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      <polygon points="3,3 117,3 97,77 23,77" />
    </svg>
  ),
  build: (groupId, ox, oy) => [
    polygon(
      [
        [0, 0],
        [120, 0],
        [100, 80],
        [20, 80],
      ],
      groupId,
      ox,
      oy,
    ),
  ],
};

export const FLOWCHART_SHAPES: readonly ShapeDefinition[] = [
  processShape,
  decision,
  terminal,
  parallelogram,
  manualInput,
  predefinedProcess,
  connector,
  offPageConnector,
  manualOperation,
  cylinder,
];
